import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from "@angular/router";
import { Observable } from "rxjs";
import { logger } from "../../util/Logger";
import { IdleTimeoutService } from "../idle-timeout.service";
import { ToastService } from "../toast.service";
import { UserService } from "../user/user.service";
import { AuthService } from "./auth.service";
import { JwtService } from "./jwt.service";

const className = "AuthGuardService";

@Injectable({
  providedIn: "root",
})
export class AuthGuardService {
  constructor(
    private authService: AuthService,
    private jwtService: JwtService,
    private userService: UserService,
    private toastService: ToastService,
    private router: Router,
    private idleTimeoutService: IdleTimeoutService,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const signature = className + ".canActivate: ";
    const fallbackUrlTree = this.router.createUrlTree(["/login"]);

    logger.silly(signature + `Entered for url [${route.url}]`);

    if (this.userService.currentUser) {
      // currentUser data is loaded
      logger.silly(signature + `Found known User[${this.userService.currentUser.id}]`);
      return !route.data["adminDirectoryOnly"] || this.userService.currentUser.isAdmin ? true : this.router.createUrlTree(["/dashboard/collaboration"]);
    } else if (this.authService.isAuthenticated()) {
      // there is a valid JWT but we need to load the currentUser data
      const userId = this.jwtService.currentJwtPayload$.value?.user.id;
      if (!userId) {
        this.toastService.showError("You must be logged in to access this page");
        return fallbackUrlTree;
      }

      const MFARequired = this.jwtService.getJWTMFARequiredString();

      if (MFARequired === "true") {
        this.toastService.showError("You must complete MFA to access this page");
        return fallbackUrlTree;
      }

      return new Promise(async (resolve) => {
        logger.silly(signature + `Loading User[${userId}]`);
        const result = await this.userService.setUser(userId, true);

        // Initialize idle timeout from config after loading user on page reload
        if (result) {
          await this.idleTimeoutService.initializeIdleTimeoutFromConfig();
          this.idleTimeoutService.watch();
        }

        resolve(result ? (!route.data["adminDirectoryOnly"] || this.userService.currentUser?.isAdmin ? true : this.router.createUrlTree(["/dashboard/collaboration"])) : fallbackUrlTree);
      });
    } else {
      logger.silly(signature + `User not logged in for url [${route.url}]`);
      this.toastService.showError("You must be logged in to access this page");
      return fallbackUrlTree;
    }
  }
}
